import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import SortTable from '../SortTable';
import ListResults from '../ListResults';
import Icon from '../Icon';
import svgEdit from '../../svgs/edit.svg';
import plus from '../../svgs/plus-white.svg';
import filter from '../../filter';
import LinkComp from '../LinkComp';
import { fetchData } from '../Service/graphQlService';
import qs from 'query-string';
import { GET_EVENTS_LIST_QUERY } from '../Graphql/Queries/EventsQuery';
import { Link } from 'react-router';
import { formatDateToMMDDYYYY } from '../../utils';

const limit = 50;

const EventsList = (props) => {
  const { location } = props;
  const query = useMemo(() => qs.parse(location.search), [location.search]);
  const client = useApolloClient();

  const [events, setEvents] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState(null);

  const parseSortOptions = useCallback((orderQuery) => {
    if (Array.isArray(orderQuery)) {
      return orderQuery.map((item) => {
        const [field, order] = item.split('.');
        return { field: field.toLowerCase(), order: order.toLowerCase() };
      });
    } else if (orderQuery) {
      const [field, order] = orderQuery.split('.');
      return [{ field: field.toLowerCase(), order: order.toLowerCase() }];
    }
    return [];
  }, []);

  const parseFilterOptions = useCallback((queryObj) => {
    const filterObject = {};
    for (const key in queryObj) {
      if (key !== 'order' && key !== 'page') {
        filterObject[key] = queryObj[key];
      }
    }
    return filterObject;
  }, []);

  const fetchEvents = useCallback(async () => {
    setIsBusy(true);
    setError(null);
    const queryClone = { ...query };
    const options = filter(queryClone, 'events');
    options.param.limit = limit;
    options.param.offset = queryClone.page ? ((Number(queryClone.page) - 1) * limit) : 0;

    const pageParams = {
      page: queryClone.page ? Number(queryClone.page) : 1,
      pageSize: limit
    };

    try {
      const res = await fetchData(GET_EVENTS_LIST_QUERY, {
        pageOptions: pageParams,
        filter: parseFilterOptions(queryClone),
        sort: parseSortOptions(queryClone.order)
      }, client);

      setEvents(res.data.getEventsWithPagination.events || []);
      setTotalCount(res.data.getEventsWithPagination.totalCount || 0);  
    } catch (err) {
      setError(err.message);
    } finally {
      setIsBusy(null);
    }
  }, [client, query, parseFilterOptions, parseSortOptions]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents, query]);

  const columns = [
    {
      name: 'View',
      textAlign: 'center',
      columnStyle: { width: 70 },
      render: (el) => {
        const basePath = arc.path.base.endsWith('/') ? arc.path.base.slice(0, -1) : arc.path.base;
        const currentSearch = window.location.search;
        const currentPath = window.location.pathname + currentSearch;

        return (
          <Link to={{
            pathname: `${basePath}/event/${el.arcimedes_row_id}`,
            state: { fromListUrl: currentPath }
          }}>
            <Icon svg={svgEdit} />
          </Link>
        );
      }
    },
    {
      code: 'date',
      name: 'Date',
      render: (el) => formatDateToMMDDYYYY(el.date)
    },
    {
      code: 'title',
      name: 'Title',
      render: (el) => el.title && el.title.length > 25 ? `${el.title.substring(0, 25)}...`: el.title,
    },
    {
      code: 'text',
      name: 'Content',
      render: (el) => el.text && el.text.length > 150 ? `${el.text.substring(0, 150)}...` : el.text,
    }
  ];

  return (
    <div className='LenderList' data-is-busy={isBusy} >
      <div className='page-rounded-corner-container'>
        <div className='page-header-container list'>
          <div className='page-header list'>
            <div className='page-title'>Events</div>
            <LinkComp
              to={{
                pathname: `${arc.path.base}event`,
                state: { fromListUrl: window.location.pathname + window.location.search }
              }}
            >
              <div className='button-new plus positive'>
                <Icon svg={plus} />
              </div>
            </LinkComp>
          </div>
          <div className='line' />
        </div>
      </div>
      <div className='page-body-container'>
            <SortTable columns={columns} items={events} query={query} />
            <ListResults
              query={query}
              pathname={location.pathname}
              error={error}
              totalCount={totalCount}
              itemCount={events ? events.length : false}
              limit={limit}
            />
      </div>
    </div>
  );
};

export default EventsList;
